/**
 * GET /order/my 가 CONFIRMED 등 일부 상태만 반환하는 백엔드와의 간극 보완:
 * 주문·결제 직후 요약을 localStorage에 두고 주문 내역 화면에서 API 목록과 병합한다.
 */
import type { ApiPaymentStatus } from '@/types/api/order';

const STORAGE_KEY = 'medicrew_recent_student_orders_v1';
const MAX_ENTRIES = 30;

export interface RecentStudentOrderSummary {
	orderId: number;
	orderNumber: string;
	totalAmount: number;
	createdAt: string;
	paymentStatus?: ApiPaymentStatus;
	/** 주문 내역 카드용 — API 목록이 비어 있을 때 보조 */
	organizationName?: string | null;
	userName?: string | null;
	productNames?: string[];
}

function readRaw(): RecentStudentOrderSummary[] {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return [];
		const parsed = JSON.parse(raw) as unknown;
		if (!Array.isArray(parsed)) return [];
		return parsed.filter(
			(x): x is RecentStudentOrderSummary =>
				x != null &&
				typeof x === 'object' &&
				typeof (x as RecentStudentOrderSummary).orderId === 'number' &&
				(x as RecentStudentOrderSummary).orderId > 0
		);
	} catch {
		return [];
	}
}

function writeRaw(entries: RecentStudentOrderSummary[]): void {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)));
	} catch {
		/* ignore */
	}
}

export function readRecentStudentOrderSummaries(): RecentStudentOrderSummary[] {
	return readRaw();
}

/** localStorage 보조 행이 API와 영구히 엇갈릴 때 카드가 쌓이지 않도록 오래된 항목 제거(기본 8일) */
export function pruneRecentStudentOrderSummariesOlderThan(
	maxAgeMs: number = 8 * 24 * 60 * 60 * 1000
): void {
	const cutoff = Date.now() - maxAgeMs;
	const prev = readRaw();
	const next = prev.filter((e) => {
		const t = new Date(e.createdAt).getTime();
		return Number.isFinite(t) && t >= cutoff;
	});
	if (next.length !== prev.length) writeRaw(next);
}

/** 주문 생성·토스 직전·결제 완료 시 호출 — 동일 orderId는 최신으로 덮어씀 */
export function pushRecentStudentOrderSummary(entry: RecentStudentOrderSummary): void {
	if (!Number.isFinite(entry.orderId) || entry.orderId <= 0) return;
	const row: RecentStudentOrderSummary = {
		orderId: Math.floor(entry.orderId),
		orderNumber: String(entry.orderNumber || entry.orderId).trim() || String(entry.orderId),
		totalAmount: Number.isFinite(entry.totalAmount) ? entry.totalAmount : 0,
		createdAt: entry.createdAt || new Date().toISOString(),
		...(entry.paymentStatus ? { paymentStatus: entry.paymentStatus } : {}),
		...(entry.organizationName != null && String(entry.organizationName).trim()
			? { organizationName: String(entry.organizationName).trim() }
			: {}),
		...(entry.userName != null && String(entry.userName).trim() ? { userName: String(entry.userName).trim() } : {}),
		...(Array.isArray(entry.productNames) && entry.productNames.length > 0
			? { productNames: entry.productNames.map((s) => String(s).trim()).filter(Boolean) }
			: {}),
	};
	const prev = readRaw();
	const same = prev.find((e) => e.orderId === row.orderId);
	const merged: RecentStudentOrderSummary = {
		...row,
		organizationName: row.organizationName ?? same?.organizationName,
		userName: row.userName ?? same?.userName,
		productNames:
			row.productNames && row.productNames.length > 0 ? row.productNames : same?.productNames,
	};
	const next = [merged, ...prev.filter((e) => e.orderId !== row.orderId)].slice(0, MAX_ENTRIES);
	writeRaw(next);
}

/**
 * API 목록과 겹치는 로컬 보조 행 제거
 * - 동일 orderId
 * - 동일 orderNumber (백엔드가 상태 변경 시 id만 바뀌는 경우 중복 카드 방지)
 */
export function pruneRecentStudentOrdersPresentInApi(
	orders: Array<{ orderId: number; orderNumber?: string | null }>
): void {
	const idSet = new Set(orders.map((o) => o.orderId).filter((id) => Number.isFinite(id) && id > 0));
	const numSet = new Set(
		orders.map((o) => String(o.orderNumber ?? '').trim()).filter((s) => s.length > 0)
	);
	if (idSet.size === 0 && numSet.size === 0) return;
	const prev = readRaw();
	const next = prev.filter((e) => {
		if (idSet.has(e.orderId)) return false;
		const n = String(e.orderNumber || '').trim();
		if (n && numSet.has(n)) return false;
		return true;
	});
	if (next.length !== prev.length) writeRaw(next);
}
